import { useEffect, useReducer } from 'react';
import useWebsocket from 'react-use-websocket';
import ServicePaths from '../bridge/utils/ServicePaths';
import {
  CPCLiveMessageType,
  type CPCProgressMessage,
  type CPCPhase,
} from '../bridge/utils/CPCLiveMessages';
import type {
  BestCalculationResult,
  ResultOverviewFile,
} from 'grts-utils/types/ProfitCalcs';
import type { TimeFrame } from 'grts-utils/types/TimeFrame';

type ProgressState = {
  runId: string | null;
  isIdle: boolean;
  isFinished: boolean;
  finishedDuration: number | null;
  baseAsset: string | null;
  comboIndex: number;
  comboTotal: number;
  currentCombo: {
    higherTimeFrame: TimeFrame;
    shorterTimeFrame: TimeFrame;
    multiOrderTimeFrame: TimeFrame;
    startedAt: number;
  } | null;
  phase: CPCPhase | null;
  phaseTotalIterations: number;
  phaseDone: number;
  phaseStartedAt: number | null;
  phasePass: number | null;
  overview: ResultOverviewFile | null;
  bestGlobal: BestCalculationResult | null;
  startedAt: number | null;
  finishedDurations: number[];
  interruptedReason: string | null;
};

const initial: ProgressState = {
  runId: null,
  isIdle: true,
  isFinished: false,
  finishedDuration: null,
  baseAsset: null,
  comboIndex: 0,
  comboTotal: 0,
  currentCombo: null,
  phase: null,
  phaseTotalIterations: 0,
  phaseDone: 0,
  phaseStartedAt: null,
  phasePass: null,
  overview: null,
  bestGlobal: null,
  startedAt: null,
  finishedDurations: [],
  interruptedReason: null,
};

const reducer = (
  state: ProgressState,
  msg: CPCProgressMessage,
): ProgressState => {
  // Discard messages for stale runs.
  if (
    'runId' in msg &&
    state.runId &&
    msg.type !== CPCLiveMessageType.RunStarted &&
    msg.runId !== state.runId
  ) {
    return state;
  }
  switch (msg.type) {
    case CPCLiveMessageType.Idle:
      // Keep "finished" state visible if the previous run just ended; only
      // fall back to idle when there is no prior run state to preserve.
      if (state.isFinished) return state;
      return { ...initial, isIdle: true };
    case CPCLiveMessageType.RunStarted:
      return {
        ...initial,
        isIdle: false,
        isFinished: false,
        finishedDuration: null,
        runId: msg.runId,
        baseAsset: msg.baseAsset,
        comboTotal: msg.numTimeFrameCombinations,
        startedAt: msg.startedAt,
      };
    case CPCLiveMessageType.ComboStarted:
      return {
        ...state,
        comboIndex: msg.comboIndex,
        comboTotal: msg.comboTotal,
        currentCombo: {
          higherTimeFrame: msg.higherTimeFrame,
          shorterTimeFrame: msg.shorterTimeFrame,
          multiOrderTimeFrame: msg.multiOrderTimeFrame,
          startedAt: msg.startedAt,
        },
        phase: null,
        phaseDone: 0,
        phaseTotalIterations: 0,
        phaseStartedAt: null,
      };
    case CPCLiveMessageType.PhaseStarted:
      return {
        ...state,
        phase: msg.phase,
        phaseTotalIterations: msg.totalIterations,
        phaseDone: 0,
        phaseStartedAt: Date.now(),
        phasePass: msg.pass ?? null,
      };
    case CPCLiveMessageType.BatchProgress: {
      const passChanged = state.phasePass !== (msg.pass ?? null);
      const phaseChanged = state.phase !== msg.phase || passChanged;
      return {
        ...state,
        phase: msg.phase,
        phaseDone: msg.done,
        phaseTotalIterations: msg.total,
        phaseStartedAt: phaseChanged ? Date.now() : state.phaseStartedAt,
        phasePass: msg.pass ?? state.phasePass,
      };
    }
    case CPCLiveMessageType.ComboFinished:
      return {
        ...state,
        finishedDurations: [...state.finishedDurations, msg.duration],
        currentCombo: null,
        phase: null,
        phaseDone: 0,
        phaseTotalIterations: 0,
        phaseStartedAt: null,
        phasePass: null,
      };
    case CPCLiveMessageType.NewBestGlobal:
      return { ...state, bestGlobal: msg.bestResult };
    case CPCLiveMessageType.HeatmapSnapshot:
      return { ...state, overview: msg.overview };
    case CPCLiveMessageType.RunFinished:
      // Keep the final snapshot visible — don't fall back to idle. The
      // dashboard renders a "Run finished" banner while bestGlobal,
      // overview, etc. stay populated. Reset happens on the next
      // RunStarted.
      return {
        ...state,
        isIdle: false,
        isFinished: true,
        finishedDuration: msg.duration,
        bestGlobal: msg.bestResult ?? state.bestGlobal,
        currentCombo: null,
        phase: null,
        phaseDone: 0,
        phaseTotalIterations: 0,
        phaseStartedAt: null,
        phasePass: null,
      };
    case CPCLiveMessageType.Interrupted:
      return { ...state, interruptedReason: msg.reason };
    default:
      return state;
  }
};

const useCPCProgressWebsocket = () => {
  const url = ServicePaths.PROFIT_CALCULATIONS.Websocket;
  const [state, dispatch] = useReducer(reducer, initial);
  const { lastMessage, readyState, getWebSocket } = useWebsocket(url, {
    shouldReconnect: () => true,
    reconnectAttempts: Infinity,
    reconnectInterval: (attempt) => Math.min(30_000, 1000 * 2 ** attempt),
  });

  useEffect(() => {
    if (!lastMessage) return;
    try {
      const msg = JSON.parse(lastMessage.data) as CPCProgressMessage;
      dispatch(msg);
    } catch {
      // ignore
    }
  }, [lastMessage]);

  useEffect(() => {
    return () => {
      const ws = getWebSocket();
      if (ws && (ws.OPEN || ws.CONNECTING)) {
        ws.close();
      }
    };
  }, [getWebSocket]);

  return { ...state, isConnected: readyState === 1 };
};

export default useCPCProgressWebsocket;
